import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Stepper,
  Step,
  StepLabel,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { Formik, Form, useFormikContext } from 'formik';
import * as yup from 'yup';
import { styled } from '@mui/material/styles';
import { Button, Typography, Input, Select } from '@/components/ui';
import { profileService, type KycStatusResponse } from '@/services/profileService';
import { notify } from '@/components/common/ToastNotification';
import { getKycStatusMessage } from '@/utils/kycMessages';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DownloadIcon from '@mui/icons-material/Download';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import toast from 'react-hot-toast';

const ProfileContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  minHeight: '100vh',
  //padding: theme.spacing(3),
  //backgroundColor: '#f8fafc',
}));

const FormCard = styled(Box)(({ theme }) => ({
  maxWidth: '100%',
  margin: '0 auto',
  backgroundColor: 'white',
  borderRadius: theme.spacing(2),
  boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)',
  overflow: 'hidden',
}));

const FormHeader = styled(Box)(({ theme }) => ({
  padding: theme.spacing(4, 4, 3, 4),
  borderBottom: `1px solid ${theme.palette.divider}`,
  textAlign: 'center',
}));

const FormBody = styled(Box)(({ theme }) => ({
  padding: theme.spacing(4),
}));

const StepContent = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(4),
  marginBottom: theme.spacing(4),
}));

const ButtonGroup = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(2),
  justifyContent: 'space-between',
  alignItems: 'center',
  marginTop: theme.spacing(4),
  paddingTop: theme.spacing(3),
  borderTop: `1px solid ${theme.palette.divider}`,
}));

const steps = ['Basic Details', 'Business Details', 'Bank Details'];

// KYC step status color codes: 0=Yellow, 1=Orange, 2=Green, 3=Red
const KYC_STATUS_COLORS: Record<number, string> = {
  0: '#FFC107', // Yellow
  1: '#FF9800', // Orange
  2: '#4CAF50', // Green
  3: '#F44336', // Red
};

const entityTypes = [
  { value: '1', label: 'Individual/Freelancers' },
  { value: '2', label: 'Sole Proprietor' },
  { value: '3', label: 'Partnership Firm' },
  { value: '4', label: 'Private Ltd/Public Ltd/OPC' },
  { value: '5', label: 'LLP' },
  { value: '6', label: 'Trust – NGO, Temples, Others' },
  { value: '7', label: 'Society/Unincorporated associations/BOI' },
  { value: '8', label: 'HUF' },
  { value: '9', label: 'Government Entity' },
  { value: '10', label: 'Co-operative Credit Society/JV/AOP' },
];

const indianStates = [
  { value: 'Andhra Pradesh', label: 'Andhra Pradesh' },
  { value: 'Arunachal Pradesh', label: 'Arunachal Pradesh' },
  { value: 'Assam', label: 'Assam' },
  { value: 'Bihar', label: 'Bihar' },
  { value: 'Chhattisgarh', label: 'Chhattisgarh' },
  { value: 'Goa', label: 'Goa' },
  { value: 'Gujarat', label: 'Gujarat' },
  { value: 'Haryana', label: 'Haryana' },
  { value: 'Himachal Pradesh', label: 'Himachal Pradesh' },
  { value: 'Jharkhand', label: 'Jharkhand' },
  { value: 'Karnataka', label: 'Karnataka' },
  { value: 'Kerala', label: 'Kerala' },
  { value: 'Madhya Pradesh', label: 'Madhya Pradesh' },
  { value: 'Maharashtra', label: 'Maharashtra' },
  { value: 'Manipur', label: 'Manipur' },
  { value: 'Meghalaya', label: 'Meghalaya' },
  { value: 'Mizoram', label: 'Mizoram' },
  { value: 'Nagaland', label: 'Nagaland' },
  { value: 'Odisha', label: 'Odisha' },
  { value: 'Punjab', label: 'Punjab' },
  { value: 'Rajasthan', label: 'Rajasthan' },
  { value: 'Sikkim', label: 'Sikkim' },
  { value: 'Tamil Nadu', label: 'Tamil Nadu' },
  { value: 'Telangana', label: 'Telangana' },
  { value: 'Tripura', label: 'Tripura' },
  { value: 'Uttar Pradesh', label: 'Uttar Pradesh' },
  { value: 'Uttarakhand', label: 'Uttarakhand' },
  { value: 'West Bengal', label: 'West Bengal' },
  { value: 'Andaman and Nicobar Islands', label: 'Andaman and Nicobar Islands' },
  { value: 'Chandigarh', label: 'Chandigarh' },
  { value: 'Dadra and Nagar Haveli and Daman and Diu', label: 'Dadra and Nagar Haveli and Daman and Diu' },
  { value: 'Delhi', label: 'Delhi' },
  { value: 'Jammu and Kashmir', label: 'Jammu and Kashmir' },
  { value: 'Ladakh', label: 'Ladakh' },
  { value: 'Lakshadweep', label: 'Lakshadweep' },
  { value: 'Puducherry', label: 'Puducherry' },
];

// Regex patterns for Indian tax & bank fields
const PAN_REGEX = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
const GSTIN_REGEX = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
/** Aadhaar number: UIDAI 12-digit format (digits only, no spaces) */
const AADHAAR_REGEX = /^[0-9]{12}$/;
const BANK_ACCOUNT_REGEX = /^[0-9]{9,18}$/;
const BANK_NAME_REGEX = /^[a-zA-Z0-9\s.\-&'()]+$/;
const IFSC_REGEX = /^[A-Z]{4}0[A-Z0-9]{6}$/;

const basicSchema = yup.object({
  company_name: yup.string().required('Company name is required'),
  website: yup.string().url('Please enter a valid URL'),
  address: yup.string().required('Address is required'),
  state: yup.string().required('State is required'),
  city: yup.string().required('City is required'),
  pincode: yup.string().required('Pincode is required'),
  entity_type: yup.string().required('Entity type is required'),
  phone_number: yup.string().required('Phone number is required'),
});

const bankSchema = yup.object({
  bank_name: yup
    .string()
    .required('Bank name is required')
    .matches(BANK_NAME_REGEX, 'Bank name can only contain letters, numbers, spaces and . - & \' ( )')
    .min(2, 'Bank name must be at least 2 characters')
    .max(100, 'Bank name must be at most 100 characters'),
  acc_holder_name: yup.string().required('Account holder name is required'),
  ifsc: yup
    .string()
    .required('IFSC code is required')
    .matches(IFSC_REGEX, 'IFSC must be 11 characters: 4 letters (bank), 0, 6 alphanumeric (branch). e.g. SBIN0001234'),
  branch: yup.string().required('Branch is required'),
  merchant_bank_number: yup
    .string()
    .required('Account number is required')
    .matches(BANK_ACCOUNT_REGEX, 'Account number must be 9–18 digits'),
  bank_proof: yup.mixed().nullable(),
});

const businessSchema = yup.object({
  pan: yup
    .string()
    .required('PAN is required')
    .matches(PAN_REGEX, 'PAN must be 10 characters: 5 letters, 4 digits, 1 letter. e.g. ABCDE1234F'),
  gstin: yup
    .string()
    .required('GSTIN is required')
    .matches(GSTIN_REGEX, 'GSTIN must be 15 characters. e.g. 27AABCU9603R1ZM'),
  aadhaar_number: yup
    .string()
    .required('Aadhaar number is required')
    .matches(AADHAAR_REGEX, 'Aadhaar number must be exactly 12 digits (UIDAI format)'),
  business_proof: yup.mixed().nullable(),
  certificate_of_incorporation: yup.mixed().nullable(),
  company_pan: yup.mixed().nullable(),
  gst_certificate: yup.mixed().nullable(),
  directors_pan_and_voterid_or_passport: yup.mixed().nullable(),
  board_resolution: yup.mixed().nullable(),
  aadhaar_document: yup.mixed().nullable(),
  declaration_of_beneficial_ownership: yup.mixed().nullable(),
});

interface FormData {
  // Basic
  merchant_name: string;
  merchant_email: string;
  phone_number: string;
  company_name: string;
  website: string;
  entity: string;
  address: string;
  state: string;
  city: string;
  pincode: string;
  entity_type: string;
  // Bank
  bank_name: string;
  acc_holder_name: string;
  ifsc: string;
  branch: string;
  merchant_bank_number: string;
  bank_proof: File | null;
  // Business
  pan: string;
  gstin: string;
  business_proof: File | null;
  certificate_of_incorporation: File | null;
  company_pan: File | null;
  gst_certificate: File | null;
  directors_pan_and_voterid_or_passport: File | null;
  board_resolution: File | null;
  aadhaar_number: string;
  aadhaar_document: File | null;
  declaration_of_beneficial_ownership: File | null;
}

/** Business document field names for file uploads */
type BusinessDocField =
  | 'bank_proof'
  | 'business_proof'
  | 'certificate_of_incorporation'
  | 'company_pan'
  | 'gst_certificate'
  | 'directors_pan_and_voterid_or_passport'
  | 'board_resolution'
  | 'aadhaar_document'
  | 'declaration_of_beneficial_ownership';

/** Format file size for display (e.g. 1024 -> "1 KB") */
function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** Get display type for file (Image or PDF) */
function getFileTypeLabel(file: File | null): string {
  if (!file) return '—';
  const ext = file.name.split('.').pop()?.toLowerCase();
  if (['jpg', 'jpeg', 'png'].includes(ext || '')) return 'Image';
  if (ext === 'pdf') return 'PDF';
  return 'Document';
}

function getStepStatus(
  stepIndex: number,
  stepLabel: string,
  kyc: KycStatusResponse | null
): number | undefined {
  if (!kyc) return undefined;
  if (stepLabel === 'Basic Details') return kyc.basic_status;
  if (stepLabel === 'Business Details') return kyc.business_status;
  if (stepLabel === 'Bank Details') return kyc.bank_status;
  return undefined;
}

const AUTO_SAVE_DEBOUNCE_MS = 800;

/** Build comparable basic-details payload */
function getBasicPayload(values: FormData) {
  const entityTypeLabel =
    entityTypes.find(t => t.value === values.entity_type)?.label || values.entity_type;
  return {
    company_name: values.company_name,
    website: values.website,
    address: values.address,
    state: values.state,
    city: values.city,
    pincode: values.pincode,
    phone_number: values.phone_number,
    entity_type: entityTypeLabel,
  };
}

/** Build comparable business-details payload (form fields + document uuids/existing) */
function getBusinessPayload(
  values: FormData,
  businessProofUuid: string,
  businessAttachmentUuids: Record<string, string>,
  initialData: Partial<any>
) {
  const d = initialData?.data || {};
  return {
    pan: values.pan,
    gstin: values.gstin,
    aadhaar_number: values.aadhaar_number,
    business_proof: businessProofUuid || (d.business_proof ? 'existing' : ''),
    certificate_of_incorporation:
      businessAttachmentUuids.certificate_of_incorporation || (d.certificate_of_incorporation ? 'existing' : ''),
    company_pan: businessAttachmentUuids.company_pan || (d.company_pan ? 'existing' : ''),
    gst_certificate: businessAttachmentUuids.gst_certificate || (d.gst_certificate ? 'existing' : ''),
    directors_pan_and_voterid_or_passport:
      businessAttachmentUuids.directors_pan_and_voterid_or_passport || (d.directors_pan_and_voterid_or_passport ? 'existing' : ''),
    board_resolution: businessAttachmentUuids.board_resolution || (d.board_resolution ? 'existing' : ''),
    aadhaar_document: businessAttachmentUuids.aadhaar_document || (d.aadhaar_document ? 'existing' : ''),
    declaration_of_beneficial_ownership:
      businessAttachmentUuids.declaration_of_beneficial_ownership || (d.declaration_of_beneficial_ownership ? 'existing' : ''),
  };
}

/** Build comparable bank-details payload */
function getBankPayload(values: FormData, bankProofUuid: string) {
  return {
    bank_name: values.bank_name,
    acc_holder_name: values.acc_holder_name,
    ifsc: values.ifsc,
    branch: values.branch,
    merchant_bank_number: values.merchant_bank_number,
    bank_proof: bankProofUuid,
  };
}

/** Auto-saves form data when input fields change (debounced). Calls API only when payload changed. Runs only when form is in edit mode. */
const ProfileAutoSave: React.FC<{
  activeStep: number;
  isFormEnabled: boolean;
  bankProofUuid: string;
  businessProofUuid: string;
  businessAttachmentUuids: Record<string, string>;
  initialData: Partial<any>;
  lastSavedBasicRef: React.MutableRefObject<Record<string, unknown> | null>;
  lastSavedBusinessRef: React.MutableRefObject<Record<string, unknown> | null>;
  lastSavedBankRef: React.MutableRefObject<Record<string, unknown> | null>;
  onSaveSuccess: (step: number, payload: Record<string, unknown>) => void;
}> = ({
  activeStep,
  isFormEnabled,
  bankProofUuid,
  businessProofUuid,
  businessAttachmentUuids,
  initialData,
  lastSavedBasicRef,
  lastSavedBusinessRef,
  lastSavedBankRef,
  onSaveSuccess,
}) => {
    const { values } = useFormikContext<FormData>();
    const debounceRef = useRef<NodeJS.Timeout | null>(null);
    const prevValuesRef = useRef<string>('');
    const isInitialMount = useRef(true);

    useEffect(() => {
      if (!isFormEnabled) return undefined; // PATCH must not run while form is disabled
      const valuesJson = JSON.stringify({
        ...values,
        bank_proof: values.bank_proof?.name,
        business_proof: values.business_proof?.name,
        certificate_of_incorporation: values.certificate_of_incorporation?.name,
        company_pan: values.company_pan?.name,
        gst_certificate: values.gst_certificate?.name,
        directors_pan_and_voterid_or_passport: values.directors_pan_and_voterid_or_passport?.name,
        board_resolution: values.board_resolution?.name,
        aadhaar_document: values.aadhaar_document?.name,
        declaration_of_beneficial_ownership: values.declaration_of_beneficial_ownership?.name,
      });
      if (isInitialMount.current) {
        isInitialMount.current = false;
        prevValuesRef.current = valuesJson;
        return undefined;
      }
      if (valuesJson === prevValuesRef.current) return undefined;
      prevValuesRef.current = valuesJson;

      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(async () => {
        debounceRef.current = null;
        try {
          if (activeStep === 0) {
            const payload = getBasicPayload(values) as Record<string, unknown>;
            if (!lastSavedBasicRef.current) return; // No baseline yet, skip to avoid unnecessary PATCH
            if (JSON.stringify(payload) === JSON.stringify(lastSavedBasicRef.current)) return;
            const formData = new FormData();
            Object.entries(payload).forEach(([k, v]) => formData.append(k, String(v ?? '')));
            await profileService.updateBasicDetails(formData);
            lastSavedBasicRef.current = payload;
            onSaveSuccess(0, payload);
          } else if (activeStep === 1) {
            const payload = getBusinessPayload(
              values,
              businessProofUuid,
              businessAttachmentUuids,
              initialData
            ) as Record<string, unknown>;
            if (!lastSavedBusinessRef.current) return; // No baseline yet, skip to avoid unnecessary PATCH
            if (JSON.stringify(payload) === JSON.stringify(lastSavedBusinessRef.current)) return;
            const formData = new FormData();
            formData.append('pan', values.pan);
            formData.append('gstin', values.gstin);
            formData.append('aadhaar_number', values.aadhaar_number);
            if (businessProofUuid) formData.append('attachment_uuid', businessProofUuid);
            if (businessAttachmentUuids.certificate_of_incorporation)
              formData.append('certificate_of_incorporation_uuid', businessAttachmentUuids.certificate_of_incorporation);
            if (businessAttachmentUuids.company_pan)
              formData.append('company_pan_uuid', businessAttachmentUuids.company_pan);
            if (businessAttachmentUuids.gst_certificate)
              formData.append('gst_certificate_uuid', businessAttachmentUuids.gst_certificate);
            if (businessAttachmentUuids.directors_pan_and_voterid_or_passport)
              formData.append('directors_pan_and_voterid_or_passport_uuid', businessAttachmentUuids.directors_pan_and_voterid_or_passport);
            if (businessAttachmentUuids.board_resolution)
              formData.append('board_resolution_uuid', businessAttachmentUuids.board_resolution);
            if (businessAttachmentUuids.aadhaar_document)
              formData.append('aadhaar_document_uuid', businessAttachmentUuids.aadhaar_document);
            if (businessAttachmentUuids.declaration_of_beneficial_ownership)
              formData.append('declaration_of_beneficial_ownership_uuid', businessAttachmentUuids.declaration_of_beneficial_ownership);
            await profileService.updateBusinessDetails(formData);
            lastSavedBusinessRef.current = payload;
            onSaveSuccess(1, payload);
          } else if (activeStep === 2) {
            const payload = getBankPayload(values, bankProofUuid) as Record<string, unknown>;
            if (!lastSavedBankRef.current) return; // No baseline yet, skip to avoid unnecessary PATCH
            if (JSON.stringify(payload) === JSON.stringify(lastSavedBankRef.current)) return;
            const formData = new FormData();
            Object.entries({
              bank_name: values.bank_name,
              acc_holder_name: values.acc_holder_name,
              ifsc: values.ifsc,
              branch: values.branch,
              merchant_bank_number: values.merchant_bank_number,
            }).forEach(([k, v]) => formData.append(k, String(v ?? '')));
            if (bankProofUuid) formData.append('attachment_uuid', bankProofUuid);
            await profileService.updateBankDetails(formData);
            lastSavedBankRef.current = payload;
            onSaveSuccess(2, payload);
          }
        } catch (error: any) {
          const errMsg = error?.response?.data?.message || error?.message || 'Failed to save changes';
          toast.error(errMsg);
        }
      }, AUTO_SAVE_DEBOUNCE_MS);

      return () => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
      };
    }, [
      values,
      activeStep,
      isFormEnabled,
      bankProofUuid,
      businessProofUuid,
      businessAttachmentUuids,
      initialData,
      lastSavedBasicRef,
      lastSavedBusinessRef,
      lastSavedBankRef,
      onSaveSuccess,
    ]);

    return null;
  };

const EMPTY_BUSINESS_UUIDS: Record<string, string> = {};

export const UpdateProfileForm: React.FC = () => {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [initialData, setInitialData] = useState<Partial<any>>({});
  const [bankProofUuid, setBankProofUuid] = useState<string>('');
  const [businessProofUuid, setBusinessProofUuid] = useState<string>('');
  const [businessAttachmentUuids, setBusinessAttachmentUuids] = useState<Record<string, string>>(EMPTY_BUSINESS_UUIDS);
  const [pendingUploadField, setPendingUploadField] = useState<BusinessDocField | null>(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const setFieldValueRef = useRef<((field: string, value: File | null) => void) | null>(null);
  const [steps, setSteps] = useState(['Basic Details', 'Business Details', 'Bank Details']);
  const [showBankDetails, setShowBankDetails] = useState(false);
  const [showActionButtons, setShowActionButtons] = useState(true);
  const formikRef = useRef<any>(null);
  const [fetchingPincode, setFetchingPincode] = useState(false);
  const [kycStatus, setKycStatus] = useState<KycStatusResponse | null>(null);
  const [isFormEnabled, setIsFormEnabled] = useState(false); // All tabs disabled by default until Edit is clicked

  // First time (no saved data): hide Edit. If profile data present: show Edit.
  const hasExistingProfile = useMemo(() => {
    const d = initialData?.data;
    if (!d) return false;
    const hasBasic = !!(d.company_name && String(d.company_name).trim());
    const hasBusiness = !!(d.pan && String(d.pan).trim());
    const hasBank = !!(d.bank_name && String(d.bank_name).trim());
    return hasBasic || hasBusiness || hasBank;
  }, [initialData]);

  const lastSavedBasicRef = useRef<Record<string, unknown> | null>(null);
  const lastSavedBusinessRef = useRef<Record<string, unknown> | null>(null);
  const lastSavedBankRef = useRef<Record<string, unknown> | null>(null);

  // Fetch city and state based on pincode
  const fetchPincodeDetails = async (pincode: string, setFieldValue: any, setFieldError: any, setFieldTouched: any) => {
    if (pincode.length !== 6) return;

    setFetchingPincode(true);
    try {
      const response = await fetch(`https://api.postalpincode.in/pincode/${pincode}`);
      const data = await response.json();

      if (data && data[0]?.Status === 'Success' && data[0]?.PostOffice?.length > 0) {
        const postOffice = data[0].PostOffice[0];
        setFieldValue('city', postOffice.District || postOffice.Name || '');
        setFieldValue('state', postOffice.State || '');
        setFieldError('pincode', undefined);
      } else {
        setFieldTouched('pincode', true, false);
        setFieldError('pincode', 'Invalid pincode. Please enter a valid Indian pincode.');
        setFieldValue('city', '');
        setFieldValue('state', '');
      }
    } catch (error) {
      console.error('Failed to fetch pincode details:', error);
      setFieldTouched('pincode', true, false);
      setFieldError('pincode', 'Failed to verify pincode. Please try again.');
    } finally {
      setFetchingPincode(false);
    }
  };

  // // Toggle Handler
  // const handleShowBankDetails = () => {
  //   setShowBankDetails(prev => {
  //     // When toggling ON, push Bank Details step
  //     if (!prev) {
  //       setSteps(prevSteps => {
  //         if (prevSteps.includes('Bank Details')) return prevSteps;
  //         return [...prevSteps, 'Bank Details'];
  //       });
  //     }
  //     return !prev;
  //   });
  // };

  // Fetch KYC status on mount
  useEffect(() => {
    const fetchKycStatus = async () => {
      try {
        const res = await profileService.getKycStatus();
        const data = res?.data as KycStatusResponse | undefined;
        if (data) {
          setKycStatus(data);
          if (!data.kyc_status) {
            const msg = data.message || 'Please complete your profile to activate your merchant account.';
            //toast.info);
          }
        }
      } catch (error) {
        console.error('Failed to fetch KYC status:', error);
      }
    };
    fetchKycStatus();
  }, []);

  useEffect(() => {
    const fetchStepData = async () => {
      setLoading(true);
      try {
        let response;
        if (activeStep === 0) {
          response = await profileService.getBasicDetails();
        } else if (activeStep === 1) {
          response = await profileService.getBusinessDetails();
        } else if (activeStep === 2) {
          response = await profileService.getBankDetails();
        }

        if (response?.data) {
          const data = (response.data as any)?.data ?? response.data;
          setInitialData(prev => ({ ...prev, ...response.data }));

          if (activeStep === 0 && data) {
            // Normalize to label so comparison with getBasicPayload() matches (it uses value -> label)
            const entityLabel =
              entityTypes.find(t => t.value === data.entity_type)?.label ||
              entityTypes.find(t => t.label === data.entity_type)?.label ||
              data.entity_type;
            lastSavedBasicRef.current = {
              company_name: data.company_name,
              website: data.website,
              address: data.address,
              state: data.state,
              city: data.city,
              pincode: data.pincode,
              phone_number: data.phone_number,
              entity_type: entityLabel,
            };
          } else if (activeStep === 1 && data) {
            lastSavedBusinessRef.current = {
              pan: data.pan ?? '',
              gstin: data.gstin ?? '',
              aadhaar_number: data.aadhaar_number ?? '',
              business_proof: data.business_proof ? 'existing' : '',
              certificate_of_incorporation: data.certificate_of_incorporation ? 'existing' : '',
              company_pan: data.company_pan ? 'existing' : '',
              gst_certificate: data.gst_certificate ? 'existing' : '',
              directors_pan_and_voterid_or_passport: data.directors_pan_and_voterid_or_passport ? 'existing' : '',
              board_resolution: data.board_resolution ? 'existing' : '',
              aadhaar_document: data.aadhaar_document ? 'existing' : '',
              declaration_of_beneficial_ownership: data.declaration_of_beneficial_ownership ? 'existing' : '',
            };
          } else if (activeStep === 2 && data) {
            lastSavedBankRef.current = {
              bank_name: data.bank_name,
              acc_holder_name: data.acc_holder_name,
              ifsc: data.ifsc,
              branch: data.branch,
              merchant_bank_number: data.merchant_bank_number,
              bank_proof: data.bank_proof ? 'existing' : '',
            };
          }
        }
      } catch (error) {
        console.error(`Failed to fetch step ${activeStep} data:`, error);
      } finally {
        setLoading(false);
      }
    };
    fetchStepData();
  }, [activeStep]);

  // Debug: Log when initialData changes
  useEffect(() => {
  }, [initialData]);

  const handleNext = () => {
    setActiveStep(prev => prev + 1);
  };
  const handleBack = () => setActiveStep(prev => prev - 1);

  /** Next Step / Complete Profile: only changes step or shows success; does NOT call PATCH. */
  const handleSubmit = async (values: FormData) => {
    const d = initialData?.data || {};

    if (activeStep === 1) {
      const mandatoryBusinessDocs: { key: BusinessDocField; label: string }[] = [
        { key: 'business_proof', label: 'Business proof document' },
        { key: 'certificate_of_incorporation', label: 'Certificate of Incorporation' },
        { key: 'company_pan', label: 'Company PAN document' },
        { key: 'gst_certificate', label: 'GST Certificate' },
        { key: 'directors_pan_and_voterid_or_passport', label: 'Directors PAN and Voter ID/Passport' },
        { key: 'board_resolution', label: 'Board Resolution' },
        { key: 'aadhaar_document', label: 'Aadhaar document' },
      ];
      const missingDocs: { key: string; label: string }[] = [];
      for (const { key, label } of mandatoryBusinessDocs) {
        const hasUuid =
          key === 'business_proof'
            ? businessProofUuid
            : businessAttachmentUuids[key as keyof typeof businessAttachmentUuids];
        const hasFile = values[key as keyof FormData] instanceof File;
        const hasExisting = !!d[key];
        if (!hasUuid && !hasFile && !hasExisting) {
          missingDocs.push({ key, label });
        }
      }
      if (missingDocs.length > 0) {
        if (formikRef.current) {
          missingDocs.forEach(({ key, label }) => {
            formikRef.current.setFieldError(key, `Please upload ${label}`);
            formikRef.current.setFieldTouched(key, true);
          });
        }
        toast.error(`Please upload ${missingDocs.length} required document(s)`);
        return;
      }
    }

    if (activeStep === 2 && !bankProofUuid && !d.bank_proof) {
      toast.error('Please upload bank proof document');
      return;
    }

    // First time (no existing profile): save current step via PATCH then advance.
    if (!hasExistingProfile) {
      setLoading(true);
      try {
        const formData = new FormData();
        if (activeStep === 0) {
          const payload = getBasicPayload(values) as Record<string, unknown>;
          Object.entries(payload).forEach(([k, v]) => formData.append(k, String(v ?? '')));
          await profileService.updateBasicDetails(formData);
          lastSavedBasicRef.current = payload;
          const res = await profileService.getBasicDetails();
          if (res?.data) setInitialData(prev => ({ ...prev, ...res.data }));
        } else if (activeStep === 1) {
          formData.append('pan', values.pan);
          formData.append('gstin', values.gstin);
          formData.append('aadhaar_number', values.aadhaar_number);
          if (businessProofUuid) formData.append('attachment_uuid', businessProofUuid);
          if (businessAttachmentUuids.certificate_of_incorporation) formData.append('certificate_of_incorporation_uuid', businessAttachmentUuids.certificate_of_incorporation);
          if (businessAttachmentUuids.company_pan) formData.append('company_pan_uuid', businessAttachmentUuids.company_pan);
          if (businessAttachmentUuids.gst_certificate) formData.append('gst_certificate_uuid', businessAttachmentUuids.gst_certificate);
          if (businessAttachmentUuids.directors_pan_and_voterid_or_passport) formData.append('directors_pan_and_voterid_or_passport_uuid', businessAttachmentUuids.directors_pan_and_voterid_or_passport);
          if (businessAttachmentUuids.board_resolution) formData.append('board_resolution_uuid', businessAttachmentUuids.board_resolution);
          if (businessAttachmentUuids.aadhaar_document) formData.append('aadhaar_document_uuid', businessAttachmentUuids.aadhaar_document);
          if (businessAttachmentUuids.declaration_of_beneficial_ownership) formData.append('declaration_of_beneficial_ownership_uuid', businessAttachmentUuids.declaration_of_beneficial_ownership);
          await profileService.updateBusinessDetails(formData);
          lastSavedBusinessRef.current = getBusinessPayload(values, businessProofUuid, businessAttachmentUuids, initialData) as Record<string, unknown>;
          const res = await profileService.getBusinessDetails();
          if (res?.data) setInitialData(prev => ({ ...prev, ...res.data }));
        } else {
          Object.entries({ bank_name: values.bank_name, acc_holder_name: values.acc_holder_name, ifsc: values.ifsc, branch: values.branch, merchant_bank_number: values.merchant_bank_number }).forEach(([k, v]) => formData.append(k, String(v ?? '')));
          if (bankProofUuid) formData.append('attachment_uuid', bankProofUuid);
          await profileService.updateBankDetails(formData);
          lastSavedBankRef.current = getBankPayload(values, bankProofUuid) as Record<string, unknown>;
          const res = await profileService.getBankDetails();
          if (res?.data) setInitialData(prev => ({ ...prev, ...res.data }));
        }
      } catch (error: any) {
        const errData = error?.response?.data?.data;
        if (errData && typeof errData === 'object' && formikRef.current) {
          const fieldErrors: Record<string, string> = {};
          Object.entries(errData).forEach(([field, messages]) => {
            const msg = Array.isArray(messages) ? messages[0] : String(messages);
            if (msg) fieldErrors[field] = msg;
          });
          if (Object.keys(fieldErrors).length > 0) formikRef.current.setErrors(fieldErrors);
        }
        toast.error(error?.response?.data?.message || error?.message || 'Failed to save');
        setLoading(false);
        return;
      }
      setLoading(false);
    }

    if (activeStep === 2) {
      const kycRes = await profileService.getKycStatus();
      if (kycRes?.data) {
        const kycData = kycRes.data as KycStatusResponse;
        setKycStatus(kycData);
        const message = getKycStatusMessage(
          kycData.basic_status,
          kycData.business_status,
          kycData.bank_status
        );
        toast.success(message);
      }
    }
    handleNext();
  };

  /** Save current step only. Called when user clicks Save (APIs run only when saving). */
  const handleSaveCurrentStep = async () => {
    const values = formikRef.current?.values as FormData | undefined;
    if (!values) return;

    setLoading(true);
    try {
      const formData = new FormData();
      if (activeStep === 0) {
        const payload = getBasicPayload(values) as Record<string, unknown>;
        // if (lastSavedBasicRef.current && JSON.stringify(payload) === JSON.stringify(lastSavedBasicRef.current)) {
        //   toast.error('No changes to save ++');
        //   return;
        // }
        Object.entries(payload).forEach(([k, v]) => formData.append(k, String(v ?? '')));
        let respose = await profileService.updateBasicDetails(formData);
        
        lastSavedBasicRef.current = payload;

        if (respose.data.success == true && respose.status == 200) {
          toast.success(respose.data.message);
        } else {
          toast.error(respose.data.error);
        }

      } else if (activeStep === 1) {
        const payload = getBusinessPayload(values, businessProofUuid, businessAttachmentUuids, initialData) as Record<string, unknown>;
        // if (lastSavedBusinessRef.current && JSON.stringify(payload) === JSON.stringify(lastSavedBusinessRef.current)) {
        //   toast.error('No changes to save --');
        //   return;
        // }
        formData.append('pan', values.pan);
        formData.append('gstin', values.gstin);
        formData.append('aadhaar_number', values.aadhaar_number);
        if (businessProofUuid) formData.append('attachment_uuid', businessProofUuid);
        if (businessAttachmentUuids.certificate_of_incorporation) formData.append('certificate_of_incorporation_uuid', businessAttachmentUuids.certificate_of_incorporation);
        if (businessAttachmentUuids.company_pan) formData.append('company_pan_uuid', businessAttachmentUuids.company_pan);
        if (businessAttachmentUuids.gst_certificate) formData.append('gst_certificate_uuid', businessAttachmentUuids.gst_certificate);
        if (businessAttachmentUuids.directors_pan_and_voterid_or_passport) formData.append('directors_pan_and_voterid_or_passport_uuid', businessAttachmentUuids.directors_pan_and_voterid_or_passport);
        if (businessAttachmentUuids.board_resolution) formData.append('board_resolution_uuid', businessAttachmentUuids.board_resolution);
        if (businessAttachmentUuids.aadhaar_document) formData.append('aadhaar_document_uuid', businessAttachmentUuids.aadhaar_document);
        if (businessAttachmentUuids.declaration_of_beneficial_ownership) formData.append('declaration_of_beneficial_ownership_uuid', businessAttachmentUuids.declaration_of_beneficial_ownership);
        let respose = await profileService.updateBusinessDetails(formData);
        lastSavedBusinessRef.current = payload;


        if (respose.data.success == true && respose.status == 200) {
          toast.success(respose.data.message);
        } else {
          toast.error(respose.data.error);
        }
      } else if (activeStep === 2) {
        const payload = getBankPayload(values, bankProofUuid) as Record<string, unknown>;
        if (lastSavedBankRef.current && JSON.stringify(payload) === JSON.stringify(lastSavedBankRef.current)) {
          toast.error('No changes to save');
          return;
        }
        Object.entries({ bank_name: values.bank_name, acc_holder_name: values.acc_holder_name, ifsc: values.ifsc, branch: values.branch, merchant_bank_number: values.merchant_bank_number }).forEach(([k, v]) => formData.append(k, String(v ?? '')));
        if (bankProofUuid) formData.append('attachment_uuid', bankProofUuid);
        let respose = await profileService.updateBankDetails(formData);
        lastSavedBankRef.current = payload;

        if (respose.data.success == true && respose.status == 200) {
          toast.success(respose.data.message);
        } else {
          toast.error(respose.data.error);
        }
      }
    } catch (error: any) {
      const errData = error?.response?.data?.data;
      if (errData && typeof errData === 'object' && formikRef.current) {
        const fieldErrors: Record<string, string> = {};
        Object.entries(errData).forEach(([field, messages]) => {
          const msg = Array.isArray(messages) ? messages[0] : String(messages);
          if (msg) fieldErrors[field] = msg;
        });
        if (Object.keys(fieldErrors).length > 0) formikRef.current.setErrors(fieldErrors);
      }
      toast.error(error?.response?.data?.message || error?.message || 'Failed to save');
    } finally {
      setLoading(false);
    }
  };

  const getValidationSchema = () => {
    switch (activeStep) {
      case 0:
        return basicSchema;
      case 1:
        return businessSchema;
      case 2:
        return bankSchema;
      default:
        return yup.object()
    }
  };

  const handleUploadClick = (type: BusinessDocField, setFieldValue: (f: string, v: File | null) => void) => {
    setFieldValueRef.current = setFieldValue;
    setPendingUploadField(type);
    fileInputRef.current?.click();
  };

  const handleFileInputChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    const field = pendingUploadField;
    setPendingUploadField(null);
    if (!file || !field) return;

    const maxSize = 25 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error('File size exceeds 25 MB limit');
      return;
    }
    const validTypes = ['.jpg', '.jpeg', '.png', '.pdf'];
    const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
    if (!validTypes.includes(fileExtension)) {
      toast.error('Invalid file type. Accepted formats: .jpg, .png, .pdf');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('document_type', field);
      formData.append('file', file);

      const response = await profileService.uploadDocument(formData);
      const uuid = (response.data as any)?.attachment_uuid ?? response.data?.attachment_uuid;
      const setFieldValue = setFieldValueRef.current;

      if (field === 'bank_proof') {
        setBankProofUuid(uuid);
        setFieldValue?.('bank_proof', file);
      } else if (field === 'business_proof') {
        setBusinessProofUuid(uuid);
        setFieldValue?.('business_proof', file);
      } else {
        setBusinessAttachmentUuids(prev => ({ ...prev, [field]: uuid }));
        setFieldValue?.(field, file);
      }

      toast.success(`${file.name} uploaded successfully`);
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || error?.message || `Failed to upload ${file.name}`;
      toast.error(errorMessage);
    } finally {
      setUploading(false);
    }
  };

  const initialValues: FormData = useMemo(
    () => ({
      merchant_name: initialData?.data?.merchant_name || '',
      merchant_email: initialData?.data?.merchant_email || '',
      phone_number: initialData?.data?.phone_number || '',
      entity_type: initialData?.data?.entity_type ? String(initialData.data.entity_type) : '',
      company_name: initialData?.data?.company_name || '',
      website: initialData?.data?.website || '',
      entity: initialData?.data?.entity || '',
      address: initialData?.data?.address || '',
      state: initialData?.data?.state || '',
      city: initialData?.data?.city || '',
      pincode: initialData?.data?.pincode || '',
      bank_name: initialData?.data?.bank_name || '',
      acc_holder_name: initialData?.data?.acc_holder_name || '',
      ifsc: initialData?.data?.ifsc || '',
      branch: initialData?.data?.branch || '',
      merchant_bank_number: initialData?.data?.merchant_bank_number || '',
      bank_proof: null,
      pan: initialData?.data?.pan || '',
      gstin: initialData?.data?.gstin || '',
      business_proof: null,
      certificate_of_incorporation: null,
      company_pan: null,
      gst_certificate: null,
      directors_pan_and_voterid_or_passport: null,
      board_resolution: null,
      aadhaar_number: initialData?.data?.aadhaar_number || '',
      aadhaar_document: null,
      declaration_of_beneficial_ownership: null,
    }),
    [initialData]
  );

  if (loading) {
    return (
      <ProfileContainer>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
          <CircularProgress />
        </Box>
      </ProfileContainer>
    );
  }

  const renderStepContent = (values: FormData, setFieldValue: any, errors: any, touched: any, setFieldError: any, setFieldTouched: any, formEnabled: boolean) => {
    const disabled = !formEnabled;
    switch (activeStep) {
      case 0:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <Input
                fullWidth
                size="small"
                label="Name"
                value={values.merchant_name}
                disabled
                sx={{
                  width: {
                    xs: '280px',
                    sm: '350px',
                    md: '420px',
                    lg: '450px',
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Input
                fullWidth
                size="small"
                label="Email"
                value={values.merchant_email}
                disabled
                sx={{
                  width: {
                    xs: '280px',
                    sm: '350px',
                    md: '420px',
                    lg: '450px',
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Input
                fullWidth
                size="small"
                label="Phone Number"
                required
                value={values.phone_number}
                disabled={disabled}
                onChange={(e) => {
                  const value = e.target.value
                    .replace(/\D/g, '')   // allow only digits
                    .slice(0, 10);        // max 10 digits

                  setFieldValue('phone_number', value);
                }}
                error={touched.phone_number && !!errors.phone_number}
                helperText={touched.phone_number && errors.phone_number}
                inputProps={{ maxLength: 10 }}
                sx={{
                  width: {
                    xs: '280px',
                    sm: '350px',
                    md: '420px',
                    lg: '450px',
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Input
                fullWidth
                size="small"
                label="Company Name"
                required
                value={values.company_name}
                disabled={disabled}
                onChange={e => setFieldValue('company_name', e.target.value)}
                error={touched.company_name && !!errors.company_name}
                helperText={touched.company_name && errors.company_name}
                sx={{
                  width: {
                    xs: '280px',
                    sm: '350px',
                    md: '420px',
                    lg: '450px',
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Select
                fullWidth
                size="small"
                label="Entity Type"
                disabled={disabled}
                required
                value={values.entity_type}
                onChange={e => setFieldValue('entity_type', e.target.value)}
                options={entityTypes}
                error={touched.entity_type && !!errors.entity_type}
                helperText={touched.entity_type && errors.entity_type}
                sx={{
                  width: {
                    xs: '280px',
                    sm: '350px',
                    md: '420px',
                    lg: '450px',
                  },
                  marginTop: '8px',
                  '& .MuiInputLabel-root': {
                    marginBottom: '4px',
                    position: 'relative',
                    display: 'block',
                  },
                  '&.Mui-disabled .MuiInputLabel-root': {
                    color: 'rgba(0, 0, 0, 0.7)',
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Input
                fullWidth
                size="small"
                label="Address"
                required
                value={values.address}
                disabled={disabled}
                onChange={e => setFieldValue('address', e.target.value)}
                error={touched.address && !!errors.address}
                helperText={touched.address && errors.address}
                sx={{
                  width: {
                    xs: '280px',
                    sm: '350px',
                    md: '420px',
                    lg: '450px',
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Input
                fullWidth
                size="small"
                label="Pincode"
                required
                value={values.pincode}
                disabled={disabled}
                onChange={e => {
                  const value = e.target.value.replace(/[^0-9]/g, '').slice(0, 6);
                  setFieldValue('pincode', value);
                  if (value.length === 6) {
                    fetchPincodeDetails(value, setFieldValue, setFieldError, setFieldTouched);
                  } else {
                    setFieldError('pincode', undefined);
                  }
                }}
                error={touched.pincode && !!errors.pincode}
                helperText={touched.pincode && errors.pincode}
                inputProps={{ maxLength: 6 }}
                sx={{
                  width: {
                    xs: '280px',
                    sm: '350px',
                    md: '420px',
                    lg: '450px',
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Input
                fullWidth
                size="small"
                label="City"
                required
                value={values.city}
                onChange={e => setFieldValue('city', e.target.value)}
                error={touched.city && !!errors.city}
                helperText={touched.city && errors.city}
                disabled={disabled || fetchingPincode}
                sx={{
                  width: {
                    xs: '280px',
                    sm: '350px',
                    md: '420px',
                    lg: '450px',
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Select
                fullWidth
                size="small"
                label="State"
                required
                value={values.state}
                onChange={e => setFieldValue('state', e.target.value)}
                options={indianStates}
                error={touched.state && !!errors.state}
                helperText={touched.state && errors.state}
                disabled={disabled || fetchingPincode}
                sx={{
                  width: {
                    xs: '280px',
                    sm: '350px',
                    md: '420px',
                    lg: '450px',
                  },
                  marginTop: '8px',
                  '& .MuiInputLabel-root': {
                    marginBottom: '4px',
                    position: 'relative',
                    display: 'block',
                  },
                  '&.Mui-disabled .MuiInputLabel-root': {
                    color: 'rgba(0, 0, 0, 0.7)',
                  },
                }}
              />
            </Grid>
          </Grid>
        );

      case 1: {
        const id = initialData?.data || {};
        const businessRows: { field: BusinessDocField; label: string; required: boolean }[] = [
          { field: 'business_proof', label: 'Business Proof', required: true },
          { field: 'certificate_of_incorporation', label: 'Certificate of Incorporation', required: true },
          { field: 'company_pan', label: 'Company PAN', required: true },
          { field: 'gst_certificate', label: 'GST Certificate', required: true },
          { field: 'directors_pan_and_voterid_or_passport', label: 'Directors PAN and Voter ID/Passport', required: true },
          { field: 'board_resolution', label: 'Board Resolution', required: true },
          { field: 'aadhaar_document', label: 'Aadhaar Document', required: true },
          { field: 'declaration_of_beneficial_ownership', label: 'Declaration of Beneficial Ownership', required: false },
        ];
        return (
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <Input
                fullWidth
                size="small"
                label="PAN"
                required
                value={values.pan}
                disabled={disabled}
                onChange={e => {
                  const value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 10);
                  setFieldValue('pan', value);
                }}
                error={touched.pan && !!errors.pan}
                helperText={touched.pan && errors.pan}
                inputProps={{ maxLength: 10 }}
                sx={{ width: { xs: '280px', sm: '350px', md: '420px', lg: '450px' } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Input
                fullWidth
                size="small"
                label="GSTIN"
                required
                value={values.gstin}
                disabled={disabled}
                onChange={e => {
                  const value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 15);
                  setFieldValue('gstin', value);
                }}
                error={touched.gstin && !!errors.gstin}
                helperText={touched.gstin && errors.gstin}
                inputProps={{ maxLength: 15 }}
                sx={{ width: { xs: '280px', sm: '350px', md: '420px', lg: '450px' } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Input
                fullWidth
                size="small"
                label="Aadhaar Number"
                required
                value={values.aadhaar_number}
                disabled={disabled}
                onChange={e => {
                  const value = e.target.value.replace(/\D/g, '').slice(0, 12);
                  setFieldValue('aadhaar_number', value);
                }}
                error={touched.aadhaar_number && !!errors.aadhaar_number}
                helperText={touched.aadhaar_number && errors.aadhaar_number}
                inputProps={{ maxLength: 12 }}
                sx={{ width: { xs: '280px', sm: '350px', md: '420px', lg: '450px' } }}
              />
            </Grid>
            <Grid item xs={12} sx={{ width: '100%', maxWidth: '100%' }}>
              <TableContainer component={Paper} variant="outlined" sx={{ mt: 2, width: '100%', maxWidth: '100%' }}>
                <Table size="small" sx={{ width: '100%', minWidth: 0, tableLayout: 'auto' }}>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: 'grey.50' }}>
                      <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Size</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {businessRows.map(({ field, label, required }) => {
                      const value = values[field] as File | null;
                      const existingUrl = id[field];
                      const uuid = field === 'business_proof' ? businessProofUuid : businessAttachmentUuids[field];
                      const hasValue = !!existingUrl || !!value || !!uuid;
                      return (
                        <TableRow key={field}>
                          <TableCell>
                            {label}
                            {required && <span style={{ color: 'red', marginLeft: 4 }}>*</span>}
                          </TableCell>
                          <TableCell>JPG, PNG, JPEG file uploads</TableCell>
                          <TableCell>File size must be less than 20 MB</TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Tooltip title="Upload">
                                <span>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleUploadClick(field, setFieldValue)}
                                    disabled={disabled || uploading}
                                    color="primary"
                                    aria-label="Upload"
                                    sx={{
                                      '&.Mui-disabled': {
                                        color: 'primary.main',
                                        opacity: 0.6,
                                        backgroundColor: 'action.hover',
                                      },
                                    }}
                                  >
                                    <CloudUploadIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                              {existingUrl && (
                                <Tooltip title="Download">
                                  <IconButton
                                    size="small"
                                    onClick={() => {
                                      const link = document.createElement('a');
                                      link.href = existingUrl;
                                      link.download = field;
                                      link.click();
                                    }}
                                    aria-label="Download"
                                  >
                                    <DownloadIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                              {hasValue && (
                                <CheckCircleIcon sx={{ color: 'success.main', fontSize: 22 }} titleAccess="Uploaded" />
                              )}
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          </Grid>
        );
      }

      case 2:
        return (
          <>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <Input
                  size="small"
                  fullWidth
                  label="AC Name"
                  required
                  value={values.acc_holder_name}
                  disabled={disabled}
                  onChange={e => setFieldValue('acc_holder_name', e.target.value)}
                  error={touched.acc_holder_name && !!errors.acc_holder_name}
                  helperText={touched.acc_holder_name && errors.acc_holder_name}
                  sx={{
                    width: {
                      xs: '280px',
                      sm: '350px',
                      md: '420px',
                      lg: '450px',
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Input
                  size="small"
                  fullWidth
                  label="AC Number"
                  required
                  value={values.merchant_bank_number}
                  disabled={disabled}
                  onChange={e => {
                    const value = e.target.value.replace(/\D/g, '').slice(0, 18);
                    setFieldValue('merchant_bank_number', value);
                  }}
                  error={touched.merchant_bank_number && !!errors.merchant_bank_number}
                  helperText={touched.merchant_bank_number && errors.merchant_bank_number}
                  sx={{
                    width: {
                      xs: '280px',
                      sm: '350px',
                      md: '420px',
                      lg: '450px',
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Input
                  size="small"
                  fullWidth
                  label="Bank Name"
                  required
                  value={values.bank_name}
                  disabled={disabled}
                  onChange={e => setFieldValue('bank_name', e.target.value)}
                  error={touched.bank_name && !!errors.bank_name}
                  helperText={touched.bank_name && errors.bank_name}
                  sx={{
                    width: {
                      xs: '280px',
                      sm: '350px',
                      md: '420px',
                      lg: '450px',
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Input
                  size="small"
                  fullWidth
                  label="Branch Name"
                  required
                  value={values.branch}
                  disabled={disabled}
                  onChange={e => setFieldValue('branch', e.target.value)}
                  error={touched.branch && !!errors.branch}
                  helperText={touched.branch && errors.branch}
                  sx={{
                    width: {
                      xs: '280px',
                      sm: '350px',
                      md: '420px',
                      lg: '450px',
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Input
                  size="small"
                  fullWidth
                  label="IFSC Code"
                  required
                  value={values.ifsc}
                  disabled={disabled}
                  onChange={e => {
                    const value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 11);
                    setFieldValue('ifsc', value);
                  }}
                  inputProps={{ maxLength: 11 }}
                  error={touched.ifsc && !!errors.ifsc}
                  helperText={touched.ifsc && errors.ifsc}
                  sx={{
                    width: {
                      xs: '280px',
                      sm: '350px',
                      md: '420px',
                      lg: '450px',
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sx={{ width: '100%', maxWidth: '100%' }}>
                <TableContainer component={Paper} variant="outlined" sx={{ mt: 2, width: '100%', maxWidth: '100%' }}>
                  <Table size="small" sx={{ width: '100%', minWidth: 0, tableLayout: 'auto' }}>
                    <TableHead>
                      <TableRow sx={{ backgroundColor: 'grey.50' }}>
                        <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Size</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Action</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell>
                          Bank Proof
                          <span style={{ color: 'red', marginLeft: 4 }}>*</span>
                        </TableCell>
                        <TableCell>JPG, PNG, JPEG, Doc file uploads</TableCell>
                        <TableCell>File size must be less than 20 MB</TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Tooltip title="Upload">
                              <span>
                                <IconButton
                                  size="small"
                                  onClick={() => handleUploadClick('bank_proof', setFieldValue)}
                                  disabled={disabled || uploading}
                                  color="primary"
                                  aria-label="Upload"
                                  sx={{
                                    '&.Mui-disabled': {
                                      color: 'primary.main',
                                      opacity: 0.6,
                                      backgroundColor: 'action.hover',
                                    },
                                  }}
                                >
                                  <CloudUploadIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                            {initialData?.data?.bank_proof && (
                              <Tooltip title="Download">
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    const link = document.createElement('a');
                                    link.href = initialData.data.bank_proof;
                                    link.download = 'bank_proof';
                                    link.click();
                                  }}
                                  aria-label="Download"
                                >
                                  <DownloadIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            {(initialData?.data?.bank_proof || values.bank_proof || bankProofUuid) && (
                              <CheckCircleIcon sx={{ color: 'success.main', fontSize: 22 }} titleAccess="Uploaded" />
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
            </Grid>
          </>
        );

      default:
        return null;
    }
  };

  return (
    <ProfileContainer>
      <FormCard>
        {/* {kycStatus && !kycStatus.kyc_status && (
          <Alert severity="info" sx={{ mx: 2, mt: 2 }}>
            {kycStatus.message}
          </Alert>
        )} */}
        {/* <Collapse in={showActionButtons}>
          <FormHeader>
            <Typography variant="h2" weight="bold" align="center" sx={{ mb: 1 }}>
              Complete Your Onboarding
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              Please activate your account to continue using our services securely.
            </Typography>

            <Button
              variant="contained"
              onClick={() => {
                handleShowBankDetails();
                // formikRef.current?.submitForm();
              }}
              sx={{ borderRadius: 2 }}
            >
              Activate Account
            </Button>

            <Button
              variant="outlined"
              onClick={() => setShowActionButtons(false)}
              sx={{ borderRadius: 2, ml: 2 }}
            >
              Remind Later
            </Button>
          </FormHeader>
        </Collapse> */}

        <FormHeader>
          <>
            <Typography variant="h4" weight="bold" sx={{ mb: 1 }}>
              Update Profile
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Complete your profile information in {steps.length} simple steps
            </Typography>
          </>
        </FormHeader>

        <FormBody>
          <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
            {steps.map((label, index) => {
              const statusCode = getStepStatus(index, label, kycStatus);
              const statusColor = statusCode !== undefined ? KYC_STATUS_COLORS[statusCode] : undefined;
              const isActive = index === activeStep;
              return (
                <Step key={label}>
                  <StepLabel
                    StepIconComponent={(props: { active?: boolean; completed?: boolean; error?: boolean; icon?: React.ReactNode }) => (
                      <Box
                        sx={{
                          width: 24,
                          height: 24,
                          borderRadius: '50%',
                          backgroundColor: statusColor ?? (props.active ? 'primary.main' : 'action.disabledBackground'),
                          border: props.active ? '2px solid' : 'none',
                          borderColor: props.active ? 'grey.800' : 'transparent',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            color: statusColor ? '#fff' : 'text.primary',
                            fontWeight: 600,
                            fontSize: '0.7rem',
                          }}
                        >
                          {props.icon}
                        </Typography>
                      </Box>
                    )}
                  >
                    {label}
                  </StepLabel>
                </Step>
              );
            })}
          </Stepper>

          <Formik
            innerRef={formikRef}
            initialValues={initialValues}
            validationSchema={getValidationSchema()}
            onSubmit={handleSubmit}
            enableReinitialize={true}
          >
            {({ values, errors, touched, setFieldValue, setFieldError, setFieldTouched }) => (
              <Form>
                <ProfileAutoSave
                  activeStep={activeStep}
                  isFormEnabled={hasExistingProfile ? isFormEnabled : false}
                  bankProofUuid={bankProofUuid}
                  businessProofUuid={businessProofUuid}
                  businessAttachmentUuids={businessAttachmentUuids}
                  initialData={initialData}
                  lastSavedBasicRef={lastSavedBasicRef}
                  lastSavedBusinessRef={lastSavedBusinessRef}
                  lastSavedBankRef={lastSavedBankRef}
                  onSaveSuccess={() => {}}
                />
                <StepContent>
                  {renderStepContent(values, setFieldValue, errors, touched, setFieldError, setFieldTouched, !hasExistingProfile || isFormEnabled)}
                </StepContent>

                <ButtonGroup>
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    {activeStep > 0 && (
                      <Button variant="outlined" onClick={handleBack} disabled={loading}>
                        Back
                      </Button>
                    )}
                    {hasExistingProfile && (
                      !isFormEnabled ? (
                        <Button
                          variant="outlined"
                          onClick={() => setIsFormEnabled(true)}
                          disabled={loading}
                          sx={{ minWidth: 100 }}
                        >
                          Edit
                        </Button>
                      ) : (
                        <Button
                          variant="outlined"
                          onClick={handleSaveCurrentStep}
                          disabled={loading}
                          loading={loading}
                          sx={{ minWidth: 100 }}
                        >
                          Save
                        </Button>
                      )
                    )}
                  </Box>

                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button
                      variant="outlined"
                      onClick={() => navigate('/dashboard')}
                      disabled={loading}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      variant="primary"
                      disabled={loading}
                      sx={{ minWidth: 120 }}
                    >
                      {activeStep === steps.length - 1 || activeStep === steps.indexOf('Bank Details')
                        ? 'Complete Profile'
                        : 'Next Step'}
                    </Button>
                  </Box>
                </ButtonGroup>
              </Form>
            )}
          </Formik>
        </FormBody>
      </FormCard>

      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf,.jpg,.jpeg,.png"
        style={{ display: 'none' }}
        onChange={handleFileInputChange}
      />
    </ProfileContainer>
  );
};

export default UpdateProfileForm;